import React, { useState, useEffect, useRef } from 'react';
import './ExploreProducts.css';
import { useDispatch, useSelector } from 'react-redux';
import { addToCart, selectSelectedCategory, setSelectedCategory, selectLanguage, setSearchQuery, selectSearchQuery, selectSelectedBrand } from '../redux/cartSlice';
import { translations } from '../utils/translations';
import useWishlist from '../hooks/useWishlist';
import { translateText } from '../utils/translator';
import { productMatchesSearch } from '../utils/productSearch';

// Fuzzy / Semantic matching helpers
const calculateFaultSimilarity = (inputFault, dbFault) => {
  if (!inputFault || !dbFault) return 0;

  const normalize = (str) => {
    return str.toLowerCase()
      .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  };

  const getTokens = (str) => {
    const stopWords = new Set(['a', 'the', 'of', 'in', 'and', 'or', 'for', 'with', 'issue', 'problem', 'repair', 'replacement', 'my', 'is', 'damaged', 'broken', 'not', 'working', 'failed', 'fault']);
    return normalize(str).split(' ')
      .filter(w => w.length > 1 && !stopWords.has(w.toLowerCase()))
      .map(w => w.trim());
  };

  // Synonym mappings for typical repairs
  const synonyms = {
    'screen': ['display', 'glass', 'lcd', 'panel', 'monitor'],
    'display': ['screen', 'glass', 'lcd', 'panel'],
    'broken': ['cracked', 'smashed', 'shattered', 'damaged', 'faulty'],
    'cracked': ['broken', 'smashed', 'shattered', 'damaged', 'faulty'],
    'battery': ['power', 'charging', 'drain', 'charge'],
    'charging': ['battery', 'power', 'charge', 'port'],
    'speaker': ['sound', 'audio', 'mic', 'microphone'],
    'sound': ['speaker', 'audio', 'volume'],
    'water': ['liquid', 'wet', 'spill', 'moisture'],
    'back': ['rear', 'glass', 'cover'],
    'camera': ['lens', 'photo', 'video', 'focus']
  };

  const inputTokens = getTokens(inputFault);
  const dbTokens = getTokens(dbFault);

  if (inputTokens.length === 0 || dbTokens.length === 0) {
    return normalize(dbFault).includes(normalize(inputFault)) ? 0.5 : 0;
  }

  let matchCount = 0;
  inputTokens.forEach(t => {
    if (dbTokens.includes(t)) {
      matchCount += 1.0;
      return;
    }
    const partialMatch = dbTokens.some(dbT => dbT.includes(t) || t.includes(dbT));
    if (partialMatch) {
      matchCount += 0.7;
      return;
    }
    const tSynonyms = synonyms[t] || [];
    for (const syn of tSynonyms) {
      if (dbTokens.includes(syn)) {
        matchCount += 0.8;
        break;
      }
      const partialSynMatch = dbTokens.some(dbT => dbT.includes(syn) || syn.includes(dbT));
      if (partialSynMatch) {
        matchCount += 0.5;
        break;
      }
    }
  });

  return matchCount / Math.max(inputTokens.length, dbTokens.length);
};

const matchesBrandFuzzy = (inputBrand, dbBrand) => {
  if (!inputBrand || !dbBrand) return false;
  const cleanInput = inputBrand.toLowerCase().trim();
  const cleanDb = dbBrand.toLowerCase().trim();
  return cleanInput.includes(cleanDb) || cleanDb.includes(cleanInput);
};

const matchesModelFuzzy = (inputModel, dbModel) => {
  if (!inputModel || !dbModel) return false;
  const cleanInput = inputModel.toLowerCase().trim().replace(/\s+/g, '');
  const cleanDb = dbModel.toLowerCase().trim().replace(/\s+/g, '');
  return cleanInput.includes(cleanDb) || cleanDb.includes(cleanInput);
};

const ExploreProducts = () => {
  const dispatch = useDispatch();
  const selectedCategory = useSelector(selectSelectedCategory);
  const selectedBrand = useSelector(selectSelectedBrand);
  const searchQuery = useSelector(selectSearchQuery) || '';
  const selectedLang = useSelector(selectLanguage) || { code: 'en' };
  const t = translations[selectedLang.code || 'en'] || translations.en;
  const { isWishlisted, toggleWishlist } = useWishlist();
  const [products, setProducts] = useState([]);
  const [isCatalogLoading, setIsCatalogLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [visibleLimit, setVisibleLimit] = useState(12);
  const [translatedProductNames, setTranslatedProductNames] = useState({});

  const [categories, setCategories] = useState([]);
  const [brandsList, setBrandsList] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState('All');

  // Service Search States
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [serviceSearchParams, setServiceSearchParams] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    brand: '',
    model: '',
    fault: ''
  });
  const [services, setServices] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const brandRef = useRef(null);
  const modelRef = useRef(null);
  const faultRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width > 1200) {
        setVisibleLimit(12); // 3 rows * 4 cols
      } else if (width > 768) {
        setVisibleLimit(9);  // 3 rows * 3 cols
      } else {
        setVisibleLimit(6);  // mobile and tablet: 3 rows * 2 cols (6 products)
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setShowAll(false);
  }, [selectedCategory, selectedBrand, searchQuery]);

  const [user, setUser] = useState(() => {
    try {
      const session = localStorage.getItem('user_session');
      return session ? JSON.parse(session) : null;
    } catch { return null; }
  });

  useEffect(() => {
    const loadSession = () => {
      try {
        const session = localStorage.getItem('user_session');
        setUser(session ? JSON.parse(session) : null);
      } catch { setUser(null); }
    };
    window.addEventListener('auth_change', loadSession);
    window.addEventListener('storage', loadSession);
    return () => {
      window.removeEventListener('auth_change', loadSession);
      window.removeEventListener('storage', loadSession);
    };
  }, []);

  // Handle initial fault from navigation
  useEffect(() => {
    const handleInitialFault = (e) => {
      const fault = e.detail || '';
      setServiceForm((prev) => ({ ...prev, fault }));
    };
    window.addEventListener('service_initial_fault_change', handleInitialFault);

    const pendingFault = localStorage.getItem('service_initial_fault');
    if (pendingFault) {
      setServiceForm((prev) => ({ ...prev, fault: pendingFault }));
      localStorage.removeItem('service_initial_fault');
    }

    return () => {
      window.removeEventListener('service_initial_fault_change', handleInitialFault);
    };
  }, []);

  // Handle click outside to close autocomplete dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      let clickedInside = false;
      if (brandRef.current && brandRef.current.contains(event.target)) clickedInside = true;
      if (modelRef.current && modelRef.current.contains(event.target)) clickedInside = true;
      if (faultRef.current && faultRef.current.contains(event.target)) clickedInside = true;

      if (!clickedInside) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Show Service Search Modal when Service category is selected
  useEffect(() => {
    if (selectedCategory && selectedCategory.toLowerCase().trim() === 'services') {
      if (!serviceSearchParams && !showServiceModal) {
        setShowServiceModal(true);
      }
    } else {
      setServiceSearchParams(null);
    }
  }, [selectedCategory, serviceSearchParams, showServiceModal]);

  useEffect(() => {
    const savedSub = localStorage.getItem('selected_subcategory');
    if (savedSub) {
      setSelectedSubcategory(savedSub);
      localStorage.removeItem('selected_subcategory'); // Consume
    } else {
      setSelectedSubcategory('All');
    }
  }, [selectedCategory]);

  useEffect(() => {
    const handleSubCategoryEvent = (e) => {
      setSelectedSubcategory(e.detail || 'All');
    };
    window.addEventListener('selected_subcategory_change', handleSubCategoryEvent);

    return () => {
      window.removeEventListener('selected_subcategory_change', handleSubCategoryEvent);
    };
  }, []);

  const filteredProducts = products.filter(p => {
    // If we have an active Services search, only show matching services
    if (selectedCategory && selectedCategory.toLowerCase().trim() === 'services' && serviceSearchParams) {
      if (!p.isService) return false;

      const brandMatch = matchesBrandFuzzy(serviceSearchParams.brand, p.brandName);
      const modelMatch = matchesModelFuzzy(serviceSearchParams.model, p.model);
      const sim = calculateFaultSimilarity(serviceSearchParams.fault, p.fault);

      p.similarityScore = sim; // Store similarity for sorting

      return brandMatch && modelMatch;
    }

    // Standard filtering rules
    const matchesBrand = selectedBrand && selectedBrand !== 'All'
      ? p.isBrandProduct && p.brand && p.brand.toLowerCase().trim() === selectedBrand.toLowerCase().trim()
      : true;
    if (p.isBrandProduct && (!selectedBrand || selectedBrand === 'All') && !searchQuery) {
      return false;
    }

    const matchesCategory = selectedCategory && selectedCategory !== 'All'
      ? p.category && p.category.toLowerCase().trim() === selectedCategory.toLowerCase().trim()
      : true;
    const matchesSubcategory = selectedSubcategory && selectedSubcategory !== 'All'
      ? p.subcategory && p.subcategory.toLowerCase().trim() === selectedSubcategory.toLowerCase().trim()
      : true;
    const matchesSearch = productMatchesSearch(p, searchQuery);
    return matchesBrand && matchesCategory && matchesSubcategory && matchesSearch;
  });

  // Sort filtered results by similarity score if Services category filter is active
  if (selectedCategory && selectedCategory.toLowerCase().trim() === 'services' && serviceSearchParams) {
    filteredProducts.sort((a, b) => (b.similarityScore || 0) - (a.similarityScore || 0));
  }

  useEffect(() => {
    setIsCatalogLoading(true);
    Promise.all([
      fetch('/api/products').then((res) => res.json()),
      fetch('/api/services').then((res) => res.json()),
      fetch('/api/brand-products').then((res) => res.json()).catch(() => []),
      fetch('/api/categories').then((res) => res.json()).catch(() => []),
      fetch('/api/brands').then((res) => res.json()).catch(() => []),
      fetch('/api/related-products').then((res) => res.json()).catch(() => [])
    ])
      .then(([productsData, servicesData, brandProductsData, categoriesData, brandsData, relatedProductsData]) => {
        setCategories(categoriesData || []);
        setBrandsList(brandsData || []);
        setServices(servicesData || []);
        const brandByProductName = new Map((brandProductsData || []).map(brandProduct => [
          (brandProduct.name || '').toLowerCase().trim().replace(/\s+/g, ' '),
          brandProduct.brand
        ]));
        const combined = [
          ...(productsData || []).map(product => ({
            ...product,
            brand: product.brand || brandByProductName.get((product.name || '').toLowerCase().trim().replace(/\s+/g, ' '))
          })),
          ...servicesData.map(s => ({
            ...s,
            rating: s.rating || 5.0,
            reviewsCount: s.reviewsCount || 0,
            isService: true
          })),
          ...(brandProductsData || []).map(product => ({ ...product, isBrandProduct: true })),
          ...(relatedProductsData || [])
        ];
        setProducts(combined);
      })
      .catch((err) => {
        console.error('Error fetching catalog data:', err);
      })
      .finally(() => {
        setIsCatalogLoading(false);
      });
  }, []);

  // Translate product names when language changes
  useEffect(() => {
    let isMounted = true;
    const targetLang = selectedLang.code || 'en';

    if (targetLang === 'en' || !products || products.length === 0) {
      setTranslatedProductNames({});
      return;
    }

    const translateNames = async () => {
      const nameMap = {};
      await Promise.all(
        products.map(async (p) => {
          const pId = p._id || p.id;
          if (pId && p.name) {
            nameMap[pId] = await translateText(p.name, targetLang, 'en');
          }
        })
      );
      if (isMounted) {
        setTranslatedProductNames(nameMap);
      }
    };

    translateNames();

    return () => {
      isMounted = false;
    };
  }, [products, selectedLang.code]);

  const availableBrands = Array.from(new Set(services.map(s => s.brandName).filter(Boolean)));
  const availableModels = Array.from(new Set(services.map(s => s.model).filter(Boolean)));
  const availableFaults = Array.from(new Set(services.map(s => s.fault).filter(Boolean)));

  const brandSuggestions = availableBrands.filter(b =>
    !serviceForm.brand || b.toLowerCase().includes(serviceForm.brand.toLowerCase())
  );
  const modelSuggestions = availableModels.filter(m =>
    !serviceForm.model || m.toLowerCase().includes(serviceForm.model.toLowerCase())
  );
  const faultSuggestions = availableFaults.filter(f =>
    !serviceForm.fault || f.toLowerCase().includes(serviceForm.fault.toLowerCase())
  );

  // Helper function to render stars
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 !== 0;

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(
          <span key={i} className="star-filled">★</span>
        );
      } else if (i === fullStars + 1 && hasHalf) {
        stars.push(
          <span key={i} className="star-half">★</span>
        );
      } else {
        stars.push(
          <span key={i} className="star-empty">★</span>
        );
      }
    }
    return stars;
  };

  const handleProductClick = (id) => {
    window.history.pushState({}, '', `/product/${id}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleAddToCart = (product, e) => {
    e.stopPropagation();

    const isService = product.isService || (product.category && product.category.toLowerCase().trim() === 'services');
    let priceToUse = product.price;
    let b2bPriceToUse = product.b2bPrice;

    if (isService) {
      const sCost = product.serviceCost !== undefined ? Number(product.serviceCost) : 0;
      priceToUse = Number(product.price) + sCost;
      b2bPriceToUse = (product.b2bPrice !== undefined ? Number(product.b2bPrice) : Number(product.price)) + sCost;
    }

    dispatch(addToCart({
      id: product._id || product.id,
      name: product.name,
      price: priceToUse,
      b2bPrice: b2bPriceToUse,
      originalPrice: isService ? undefined : product.originalPrice,
      image: product.image,
      quantity: 1,
      category: product.category,
      shippingCharges: product.shippingCharges,
      deliveryTime: product.deliveryTime
    }));

    import('sweetalert2').then((Swal) => {
      const Toast = Swal.default.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.default.stopTimer);
          toast.addEventListener('mouseleave', Swal.default.resumeTimer);
        }
      });
      Toast.fire({
        icon: 'success',
        title: `${product.name} added to cart!`
      });
    });
  };

  const handleBuyNow = (product, e) => {
    e.stopPropagation();

    const isService = product.isService || (product.category && product.category.toLowerCase().trim() === 'services');
    let priceToUse = product.price;
    let b2bPriceToUse = product.b2bPrice;

    if (isService) {
      const sCost = product.serviceCost !== undefined ? Number(product.serviceCost) : 0;
      priceToUse = Number(product.price) + sCost;
      b2bPriceToUse = (product.b2bPrice !== undefined ? Number(product.b2bPrice) : Number(product.price)) + sCost;
    }

    dispatch(addToCart({
      id: product._id || product.id,
      name: product.name,
      price: priceToUse,
      b2bPrice: b2bPriceToUse,
      originalPrice: isService ? undefined : product.originalPrice,
      image: product.image,
      quantity: 1,
      category: product.category,
      shippingCharges: product.shippingCharges,
      deliveryTime: product.deliveryTime
    }));
    window.history.pushState({}, '', '/cart');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const getCategoryTranslation = (name) => {
    if (!name) return '';
    const lower = name.toLowerCase().trim();
    if (lower === 'mobile phone') return t.categoryMobilePhone || name;
    if (lower === 'accessories') return t.categoryAccessories || name;
    if (lower === 'gadget') return t.categoryFormat || t.categoryGadget || name;
    if (lower === 'service' || lower === 'services') return t.categoryService || name;
    if (lower === 'parts') return t.categoryParts || name;
    if (lower === 'computers') return t.categoryComputers || name;
    if (lower === 'offers') return t.categoryOffers || name;
    if (lower === 'used phone') return t.categoryUsedPhone || name;
    if (lower === 'electronics') return t.categoryElectronics || name;
    return name;
  };

  const activeCategoryObj = categories.find(cat => (cat.name || '').toLowerCase().trim() === (selectedCategory || '').toLowerCase().trim());
  const activeSubcategories = activeCategoryObj ? (activeCategoryObj.subcategories || []) : [];

  return (
    <section id="explore-products" className="products-section-wrapper">
      <div className="products-section-container">
        {/* Section Header */}
        <div className="section-header-block">
          <div className="section-label-container" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="label-badge"></div>
            <span className="label-text">{t.ourProductsHeading}</span>
            {selectedCategory && selectedCategory !== 'All' && (
              <span className="active-category-pill" style={{
                backgroundColor: '#fff5f5',
                color: '#be1e2d',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                border: '1px solid rgba(190, 30, 45, 0.15)'
              }}>
                {t.categories}: {getCategoryTranslation(selectedCategory)}
                <button
                  onClick={() => dispatch(setSelectedCategory('All'))}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#be1e2d',
                    fontSize: '16px',
                    lineHeight: '1',
                    cursor: 'pointer',
                    padding: '0 2px',
                    fontWeight: '700'
                  }}
                  title="Clear filter"
                >
                  &times;
                </button>
              </span>
            )}
          </div>
          <div className="section-title-row">
            <h2 className="section-title">
              {selectedCategory && selectedCategory !== 'All' ? getCategoryTranslation(selectedCategory) : t.exploreOurProducts}
            </h2>
            <div className="search-bar-wrapper">
              <input
                type="text"
                placeholder={t.searchPlaceholder || "Search products..."}
                value={searchQuery}
                onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                className="search-bar-input"
              />
              <span className="search-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </span>
              {searchQuery && (
                <button
                  onClick={() => dispatch(setSearchQuery(''))}
                  className="search-clear-btn"
                  title="Clear search"
                >
                  &times;
                </button>
              )}
            </div>
          </div>
        </div>

        {selectedCategory && selectedCategory.toLowerCase().trim() === 'services' && serviceSearchParams && (
          <div className="service-search-banner animate-in">
            <div className="service-banner-content-wrapper">
              <div className="service-banner-left-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#be1e2d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                </svg>
              </div>
              <div className="service-banner-info">
                <span className="service-banner-title">Service Search Active</span>
                <div className="service-banner-tags">
                  <span className="service-banner-chip brand">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px' }}>
                      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                    </svg>
                    {serviceSearchParams.brand}
                  </span>
                  <span className="service-banner-chip model">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px' }}>
                      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                    </svg>
                    {serviceSearchParams.model}
                  </span>
                </div>
                <span className="service-banner-fault">
                  <span className="service-banner-fault-label">Reported Issue:</span>
                  <span className="service-banner-fault-value">"{serviceSearchParams.fault}"</span>
                </span>
              </div>
            </div>
            <div className="service-banner-actions">
              <button className="service-banner-btn edit" onClick={() => setShowServiceModal(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }}>
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
                Modify Search
              </button>
              <button className="service-banner-btn reset" onClick={() => { setServiceSearchParams(null); dispatch(setSelectedCategory('All')); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }}>
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Clear Search
              </button>
            </div>
          </div>
        )}

        {/* Product Cards Grid (3 Rows of 4 Columns) */}
        {selectedCategory && selectedCategory !== 'All' && activeSubcategories.length > 0 && (
          <div className="subcategory-filter-container animate-fade-in" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
            marginBottom: '2rem',
            paddingBottom: '0.75rem',
            borderBottom: '1px solid #edf2f7'
          }}>
            <span style={{ fontSize: '0.9rem', color: '#718096', fontWeight: '600', marginRight: '8px' }}>
              Subcategories:
            </span>
            <button
              onClick={() => setSelectedSubcategory('All')}
              style={{
                background: selectedSubcategory === 'All' ? '#be1e2d' : '#f7fafc',
                color: selectedSubcategory === 'All' ? '#ffffff' : '#4a5568',
                border: selectedSubcategory === 'All' ? '1px solid #be1e2d' : '1px solid #e2e8f0',
                padding: '6px 16px',
                borderRadius: '50px',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              All
            </button>
            {activeSubcategories.map((sub) => (
              <button
                key={sub}
                onClick={() => setSelectedSubcategory(sub)}
                style={{
                  background: selectedSubcategory === sub ? '#be1e2d' : '#f7fafc',
                  color: selectedSubcategory === sub ? '#ffffff' : '#4a5568',
                  border: selectedSubcategory === sub ? '1px solid #be1e2d' : '1px solid #e2e8f0',
                  padding: '6px 16px',
                  borderRadius: '50px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                {sub}
              </button>
            ))}
          </div>
        )}

        {isCatalogLoading ? (
          <div className="catalog-loading" role="status" aria-live="polite">
            <div className="catalog-loading-spinner"></div>
            <span>Loading products...</span>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="no-products-category" style={{
            textAlign: 'center',
            padding: '3rem 1.5rem',
            background: '#f8fafc',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            color: '#718096'
          }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '1rem', color: '#a0aec0' }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#2d3748', fontSize: '1.25rem', fontWeight: '700' }}>{t.noProductsFound}</h3>
            <p style={{ margin: 0, fontSize: '0.95rem' }}>We couldn't find any products in the "{selectedCategory}" category right now.</p>
          </div>
        ) : (
          <div className="products-grid-four">
            {(showAll ? filteredProducts : filteredProducts.slice(0, visibleLimit)).map((prod) => (
              <div key={prod._id || prod.id} className="product-card">
                <div className="product-image-container">
                  {/* Action Buttons Overlay */}
                  <div className="product-actions-overlay">
                    <button
                      className={`action-btn wishlist-btn${isWishlisted(prod._id || prod.id) ? ' wishlisted' : ''}`}
                      aria-label={isWishlisted(prod._id || prod.id) ? 'Remove from Wishlist' : 'Add to Wishlist'}
                      onClick={(e) => { e.stopPropagation(); toggleWishlist({ ...prod, productId: prod._id || prod.id }); }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill={isWishlisted(prod._id || prod.id) ? '#be1e2d' : 'none'} stroke={isWishlisted(prod._id || prod.id) ? '#be1e2d' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'fill 0.2s ease, stroke 0.2s ease' }}>
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                      </svg>
                    </button>
                    <button
                      className="action-btn"
                      aria-label="Quick View"
                      onClick={() => handleProductClick(prod._id || prod.id)}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>
                  </div>

                  {/* Product Image */}
                  <div className="product-img-wrapper" onClick={() => handleProductClick(prod._id || prod.id)} style={{ cursor: 'pointer', position: 'relative' }}>
                    <img src={prod.image} alt={prod.name} className="product-card-img" />
                    {(() => {
                      const brandMatch = brandsList.find(b => (b.name || '').toLowerCase() === (prod.brandName || prod.brand || '').toLowerCase());
                      if (brandMatch && (brandMatch.image || brandMatch.logo)) {
                        const logoSrc = brandMatch.image || brandMatch.logo;
                        return <img src={logoSrc} alt={brandMatch.name} title={brandMatch.name} style={{ position: 'absolute', top: '10px', right: '10px', width: '40px', height: '40px', objectFit: 'contain', backgroundColor: 'rgba(255,255,255,0.9)', padding: '4px', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} />;
                      }
                      return null;
                    })()}
                  </div>
                </div>

                {/* Product Info */}
                <div className="product-info">
                  <span className="product-cat">{getCategoryTranslation(prod.category)}</span>
                  <h3 className="product-name" title={translatedProductNames[prod._id || prod.id] || prod.name} onClick={() => handleProductClick(prod._id || prod.id)} style={{ cursor: 'pointer' }}>{translatedProductNames[prod._id || prod.id] || prod.name}</h3>

                  {/* Rating & Reviews */}
                  <div className="product-rating-row">
                    <div className="stars-container">{renderStars(prod.rating)}</div>
                    <span className="reviews-count">({prod.rating})</span>
                  </div>

                  {/* Price block */}
                  <div className="product-price-row" style={prod.isService ? { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px', marginTop: 'auto' } : {}}>
                    {prod.isService ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '14px', color: '#718096', fontWeight: '600' }}>Price:</span>
                          {(() => {
                            const isB2b = user && user.isB2B;
                            if (isB2b && prod.b2bPrice) {
                              return (
                                <>
                                  <span className="current-price" style={{ fontSize: '16px', color: '#be1e2d' }}>€{prod.b2bPrice}</span>
                                  <span style={{ fontSize: '11px', color: '#718096', marginLeft: '4px', fontWeight: 'normal' }}>(excl. VAT)</span>
                                  <span className="original-price" style={{ textDecoration: 'line-through', fontSize: '0.85rem', color: '#a0aec0' }}>€{prod.price}</span>
                                  <span className="b2b-price-badge" style={{
                                    fontSize: '0.65rem',
                                    fontWeight: '700',
                                    backgroundColor: '#be1e2d',
                                    color: 'white',
                                    padding: '1px 5px',
                                    borderRadius: '4px',
                                    textTransform: 'uppercase'
                                  }}>B2B Price</span>
                                </>
                              );
                            }
                            return (
                              <>
                                <span className="current-price" style={{ fontSize: '16px' }}>€{prod.price}</span>
                                <span style={{ fontSize: '11px', color: '#718096', marginLeft: '4px', fontWeight: 'normal' }}>(excl. VAT)</span>
                                {prod.originalPrice && (
                                  <span className="original-price">€{prod.originalPrice}</span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '14px', color: '#718096', fontWeight: '600' }}>Service Cost:</span>
                          <span className="current-price">€{prod.serviceCost}</span>
                        </div>
                      </>
                    ) : (() => {
                      const isB2b = user && user.isB2B;
                      if (isB2b && prod.b2bPrice) {
                        return (
                          <>
                            <span className="current-price" style={{ color: '#be1e2d' }}>€{prod.b2bPrice}</span>
                            <span style={{ fontSize: '11px', color: '#718096', marginLeft: '4px', fontWeight: 'normal' }}>(excl. VAT)</span>
                            <span className="original-price" style={{ textDecoration: 'line-through', fontSize: '0.85rem', color: '#a0aec0', marginLeft: '6px' }}>€{prod.price}</span>
                            <span className="b2b-price-badge" style={{
                              fontSize: '0.65rem',
                              fontWeight: '700',
                              backgroundColor: '#be1e2d',
                              color: 'white',
                              padding: '1px 5px',
                              borderRadius: '4px',
                              marginLeft: '6px',
                              verticalAlign: 'middle',
                              textTransform: 'uppercase'
                            }}>B2B Price</span>
                          </>
                        );
                      }
                      return (
                        <>
                          <span className="current-price">€{prod.price}</span>
                          <span style={{ fontSize: '11px', color: '#718096', marginLeft: '4px', fontWeight: 'normal' }}>(excl. VAT)</span>
                          {prod.originalPrice && (
                            <span className="original-price">€{prod.originalPrice}</span>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Action Buttons */}
                  <div className="product-card-buttons">
                    <button
                      className="btn-add-to-cart"
                      aria-label={t.addToCart}
                      onClick={(e) => handleAddToCart(prod, e)}
                      disabled={!prod.isService && (prod.stockStatus === 'Out of Stock' || prod.quantity <= 0)}
                      style={(!prod.isService && (prod.stockStatus === 'Out of Stock' || prod.quantity <= 0)) ? { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#718096' } : {}}
                    >
                      {(!prod.isService && (prod.stockStatus === 'Out of Stock' || prod.quantity <= 0)) ? t.outOfStock : t.addToCart}
                    </button>
                    <button
                      className="btn-buy-now"
                      aria-label={t.buyNow}
                      onClick={(e) => handleBuyNow(prod, e)}
                      disabled={!prod.isService && (prod.stockStatus === 'Out of Stock' || prod.quantity <= 0)}
                      style={(!prod.isService && (prod.stockStatus === 'Out of Stock' || prod.quantity <= 0)) ? { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#718096' } : {}}
                    >
                      {t.buyNow}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {!showAll && filteredProducts.length > visibleLimit && (
          <>
            <br /><br />
            {/* Centered View All Products Button */}
            <div className="view-all-products-container">
              <button className="view-all-products-btn" onClick={() => setShowAll(true)}>
                View All Products
              </button>
            </div>
          </>
        )}
      </div>
      {showServiceModal && (
        <div className="service-modal-overlay">
          <div className="service-modal-card">
            <button className="service-modal-close" onClick={() => {
              setShowServiceModal(false);
              if (!serviceSearchParams) {
                dispatch(setSelectedCategory('All'));
              }
            }}>&times;</button>
            <div className="service-modal-header">
              <div className="service-modal-title-row">
                <div className="service-title-badge">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                  </svg>
                </div>
                <h3 className="service-modal-title">Search Device Services</h3>
              </div>
              <p className="service-modal-subtitle">Tell us about your device issue, and we will find the right repair service for you.</p>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              if (serviceForm.brand && serviceForm.model && serviceForm.fault) {
                setServiceSearchParams({ ...serviceForm });
                setShowServiceModal(false);
              }
            }}>
              <div className="service-form-group" ref={brandRef}>
                <label className="service-form-label">Brand Name</label>
                <div className="service-input-container">
                  <input
                    type="text"
                    placeholder="e.g. Apple, Samsung, Google"
                    className="service-form-input"
                    value={serviceForm.brand}
                    onChange={(e) => {
                      setServiceForm({ ...serviceForm, brand: e.target.value });
                      setActiveDropdown('brand');
                    }}
                    onFocus={() => setActiveDropdown('brand')}
                    required
                  />
                  <span className="service-input-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                      <line x1="7" y1="7" x2="7.01" y2="7"></line>
                    </svg>
                  </span>
                  {activeDropdown === 'brand' && brandSuggestions.length > 0 && (
                    <div className="service-suggestions-dropdown">
                      {brandSuggestions.map((brand, index) => (
                        <div
                          key={index}
                          className="service-suggestion-item"
                          onClick={() => {
                            setServiceForm({ ...serviceForm, brand });
                            setActiveDropdown(null);
                          }}
                        >
                          {brand}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="service-form-group" ref={modelRef}>
                <label className="service-form-label">Model</label>
                <div className="service-input-container">
                  <input
                    type="text"
                    placeholder="e.g. iPhone 14 Pro, Galaxy S23"
                    className="service-form-input"
                    value={serviceForm.model}
                    onChange={(e) => {
                      setServiceForm({ ...serviceForm, model: e.target.value });
                      setActiveDropdown('model');
                    }}
                    onFocus={() => setActiveDropdown('model')}
                    required
                  />
                  <span className="service-input-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                      <line x1="12" y1="18" x2="12.01" y2="18"></line>
                    </svg>
                  </span>
                  {activeDropdown === 'model' && modelSuggestions.length > 0 && (
                    <div className="service-suggestions-dropdown">
                      {modelSuggestions.map((model, index) => (
                        <div
                          key={index}
                          className="service-suggestion-item"
                          onClick={() => {
                            setServiceForm({ ...serviceForm, model });
                            setActiveDropdown(null);
                          }}
                        >
                          {model}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="service-form-group" ref={faultRef}>
                <label className="service-form-label">Fault / Issue Description</label>
                <div className="service-input-container">
                  <textarea
                    placeholder="e.g. cracked front glass, screen not turning on, battery draining fast"
                    className="service-form-input"
                    value={serviceForm.fault}
                    onChange={(e) => {
                      setServiceForm({ ...serviceForm, fault: e.target.value });
                      setActiveDropdown('fault');
                    }}
                    onFocus={() => setActiveDropdown('fault')}
                    required
                  ></textarea>
                  <span className="service-input-icon" style={{ alignSelf: 'flex-start', marginTop: '14px' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                    </svg>
                  </span>
                  {activeDropdown === 'fault' && faultSuggestions.length > 0 && (
                    <div className="service-suggestions-dropdown">
                      {faultSuggestions.map((fault, index) => (
                        <div
                          key={index}
                          className="service-suggestion-item"
                          onClick={() => {
                            setServiceForm({ ...serviceForm, fault });
                            setActiveDropdown(null);
                          }}
                        >
                          {fault}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button type="submit" className="service-submit-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                Find Matching Services
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default ExploreProducts;
